import Alert from '@mui/material/Alert';
import AssistantGrounding from 'ai-assistant/grounding/assistant-grounding';
import { AssistantGroundingState } from 'ai-assistant/grounding/assistant-grounding-state';
import React from 'react';

/**
 * What the notice says for each state that has something to say.
 *
 * Two sentences, no counts. A reader deciding whether to trust an answer needs to know that
 * part of it was not checked; how many claims that was is a number for the metrics, and
 * putting it here invites the wrong question - "is three bad?" - in place of the right one.
 *
 * Neither notice promises a mark to look for any more. Both states widened with #5209: an
 * answer is unverified when it states something no source covers, which carries no citation
 * and so nothing to withdraw, and it is unverifiable when the server could not decide about a
 * sentence even though every judge answered. Telling a reader to look for a ? that is not
 * there sends them to conclude the notice was a mistake.
 */
const NOTICE: Partial<Record<AssistantGroundingState, string>> = {
  [AssistantGroundingState.Unverified]:
    'Parts of this answer are not supported by the sources it cites. Some are marked ?, and '
      + 'some carry no citation to mark.',
  [AssistantGroundingState.Unverifiable]:
    'This answer could not be checked against its sources. Part of it has not been verified.',
};

type GroundingNoticeProps = {
  grounding: AssistantGrounding;
};

/**
 * Says, once per answer, that the server did not stand behind all of it.
 *
 * The `[[?]]` marks say which claims; this says that there were any, which is the part a
 * reader skimming an answer would otherwise miss - and it is the only thing an unverifiable
 * turn can say at all, because a judge that never ruled produced no marks to see.
 */
export default function GroundingNotice(props: Readonly<GroundingNoticeProps>) {
  const { grounding } = props;
  const message = NOTICE[grounding.state];
  if (!message) {
    return null;
  }

  return (
    <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
      {message}
    </Alert>
  );
}
